import { useDispatch, useSelector } from "react-redux";

import useClearAndCloseRowsSelection from "app/pages/sales-intelligence/hooks/useClearAndCloseRowsSelection";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { selectExcelQuotaRemaining } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import LegacyWebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/LegacyWebsitesExcelExportPanel/LegacyWebsitesExcelExportPanelView";
import WebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/WebsitesExcelExportPanelView";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";

import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import { COMPETITIVE_LANDSCAPE_TABLE_KEY, COMPETITIVE_LANDSCAPE_TABLE_PROPERTY } from "../../configuration/constants";
import useCompetitiveQueryParams from "../../hooks/useCompetitiveQueryParams";
import exportSelectionDropdown from "../../models/export/exportSelectionDropdown";
import { tableRowsSelectionToggledAction } from "../../store/action-creators";
import { downloadExcelThunk, downloadExcelTopNDomainsThunk } from "../../store/effects";
import {
    selectCheckedWebsites,
    selectCompetitiveResultsCount,
    selectIsExcelDownloading,
    selectIsFetchingResults,
} from "../../store/selectors";

const ExcelCompetitiveExportPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const excelQuotaRemaining = useSelector(selectExcelQuotaRemaining);
    const numberOfResults = useSelector(selectCompetitiveResultsCount);
    const isFetchingResults = useSelector(selectIsFetchingResults);
    const selectedWebsites = useSelector(selectCheckedWebsites);
    const isExportInProgress = useSelector(selectIsExcelDownloading);

    const exportCostState = useLeadsExportCost({ exportType: ExportCostTypeItem.Competitors });
    const { params } = useCompetitiveQueryParams();
    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection(
        tableRowsSelectionToggledAction,
        COMPETITIVE_LANDSCAPE_TABLE_KEY,
        COMPETITIVE_LANDSCAPE_TABLE_PROPERTY,
    );

    function handleDeactivate() {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    }

    function handleSelectionChange(item: Readonly<SelectionDropdownItem>) {
        if (item.id === ManualSelectionItem.ID) {
            return dispatch(tableRowsSelectionToggledAction(true));
        }

        clearAndCloseRowsSelection();
    }

    async function handleSubmit(item: Readonly<SelectionDropdownItem>) {
        trackingService.trackMultiSelectorExportClick("excel", excelQuotaRemaining, item.alias, item.value);
        handleDeactivate();

        await dispatch(
            item.id === ManualSelectionItem.ID
                ? downloadExcelThunk(selectedWebsites, params)
                : downloadExcelTopNDomainsThunk(item.value, params),
        );
    }

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesExcelExportPanelView
                isDisabled={isFetchingResults || isExportInProgress}
                isLoading={isFetchingResults || isExportInProgress}
                numberOfSelectedItems={selectedWebsites.length}
                numberOfRemainingExports={excelQuotaRemaining}
                numberOfResults={numberOfResults ?? 0}
                numberOfTableColumns={0}
                selectionDropdown={exportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={handleSelectionChange}
                onSubmit={handleSubmit}
                exportType={ExportCostTypeItem.Competitors}
                exportCostState={exportCostState}
            />
        );
    }

    return (
        <LegacyWebsitesExcelExportPanelView
            isDisabled={isFetchingResults || isExportInProgress}
            isLoading={isFetchingResults || isExportInProgress}
            numberOfSelectedItems={selectedWebsites.length}
            numberOfRemainingExports={excelQuotaRemaining}
            numberOfResults={numberOfResults ?? 0}
            numberOfTableColumns={0}
            selectionDropdown={exportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default ExcelCompetitiveExportPanelContainer;
